// @flow
/* eslint-disable no-shadow */
import classnames from 'classnames';
import React, { useState, useEffect } from 'react';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Avatar from '@material-ui/core/Avatar';
import Badge from '@material-ui/core/Badge';
import Hidden from '@material-ui/core/Hidden';
import Typography from '@material-ui/core/Typography';
import * as Sentry from '@sentry/react';
// $FlowFixMe
import { withRouter, useLocation } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import MenuOutlinedIcon from '@material-ui/icons/MenuOutlined';
import { withTheme, withStyles } from '@material-ui/core/styles';
import makeComponentTrashable from 'trashable-react';
import { connect } from 'react-redux';
import { withSnackbar } from 'notistack';
import ParticipantList from '../ParticipantList';

import AvaLogoImg from '../../../assets/images/logo.png';
import { isDefaultRoomId } from '../../utils/ws-v1';
import ScribeSpeakerAudioSwitchContainer from '../../modules/scribe/ScribeSpeakerAudioSwitchContainer';
import AudioChunkBufferContainer from '../../modules/scribe/AudioChunkBufferContainer';
import UpdateConvoMeta from '../../modules/scribe/UpdateConvoMeta';
import { BackendBadge, getBadge } from '../../modules/scribe/BackendBadge';
import CallListContainer from '../../modules/scribe/CallListContainer';
import AddParticipant from '../../modules/scribe/AddParticipant';
import FullScreenHamburger from '../../modules/scribe/FullScreenHamburger';
import SelectAccuracyButton from '../../modules/scribe/SelectAccuracyButton';
import { isHost } from '../../utils/status';
import { getIsMac } from '../../utils/electron';
import { shouldShowNewSidebarAndHeader } from '../../utils/layout';
import { clearScribeConversationState, firebaseLogout, toggleSideBar, updateFullScreen } from '../../actions';
import Offline from '../Network/Offline';
import i18next from 'i18next';

import HomeView from './HomeView';
import JoinConversationButton from './JoinConversation';

const getConversationModeColor = (conversationMode: any) => {
  switch (conversationMode) {
    case 'public-muted':
      return 'darkgreen';
    case 'public':
      return 'darkhaki';
    case 'private':
      return 'tomato';
    case 'group':
      return 'steelblue';
    default:
      return 'red';
  }
};

const hideHeaderButtonsText = (width: number, participantCount: number) => {
  // width of logo + hamburger
  const logoWidth = 300;
  // aprox width of buttons based on language
  const buttonsWidth = i18next.language === 'fr' ? 500 : 430;
  // width of all avatars
  const avatarWidth = participantCount * 52;

  return logoWidth + buttonsWidth + avatarWidth > width;
};

const styles = theme => {
  return {
    appBar: {
      boxShadow: 'none',
      borderBottom: `1px solid ${theme.palette.border}`,
    },
    signIn: {
      '&:hover': {
        color: theme.palette.text.primary,
      },
      color: theme.palette.text.primary,
      fontWeight: 700,
      marginRight: 4,
    },
    windowsTitlebarButton: {
      '&:focus': {
        outline: 'none',
      },
      background: 'transparent',
      border: 'none',
      cursor: 'default',
      fontSize: 12,
      height: '100%',
      padding: 0,
      WebkitAppearance: 'none',
      width: 46,
    },
    windowsTitlebarButtonLight: {
      '&:hover': {
        background: 'rgba(23,23,23,0.1)',
      },
      '&:active': {
        background: 'rgba(23,23,23,0.2)',
      },
      '&>svg>g': {
        fill: '#171717',
      },
    },
    windowsTitlebarButtonDark: {
      '&:hover': {
        background: 'rgba(255,255,255,0.1)',
      },
      '&:active': {
        background: 'rgba(255,255,255,0.2)',
      },
      color: '#FFFFFF',
    },
    windowsTitlebarCloseButton: {
      '&:hover': {
        background: '#E81123',
        '&>svg>g': {
          fill: '#FFFFFF',
        },
      },
      '&:active': {
        background: '#F1707A',
        '&>svg>g': {
          fill: '#171717',
        },
      },
    },
    scribeLogo: {
      margin: '0px',
      marginBottom: '5px',
      marginLeft: '10px',
      '--webkit-app-region': 'drag',
    },
    logo: {
      width: '100px',
    },
    joinConversation: {
      display: 'flex',
      alignItems: 'center',
    },
    existingRoom: {
      color: theme.palette.type === 'light' ? theme.palette.ava.dark1 : theme.palette.ava.white,
      fontSize: 24,
      lineHeight: '31.2px',
      [theme.breakpoints.down('xs')]: {
        display: 'none',
      },
    },
    arrowRight: {
      marginRight: 11,
      color: theme.palette.type === 'light' ? theme.palette.ava.dark1 : theme.palette.ava.white,
      fontSize: 24,
      [theme.breakpoints.down('xs')]: {
        display: 'none',
      },
    },
  };
};

const BadgeConnect = withStyles(() => ({
  badge: {
    backgroundColor: props => props.badeColor,
  },
}))(Badge);

function ScribeHeader({
  authenticating,
  backends,
  classes,
  currentPlatform,
  displayShortcuts,
  displayable,
  electronCaptionMode,
  features,
  firebaseUser,
  fullScreen,
  inTwilioCalls,
  isInConversation,
  lightView,
  scribe,
  speakerListened,
  speakers,
  status,
  t,
  theme,
  toggleSideBar,
  user,
  updateFullScreen,
  className,
  history,
  sidebarOpen,
}: {
  authenticating: boolean,
  backends: Object,
  classes: Object,
  currentPlatform: string,
  displayShortcuts: boolean,
  displayable: Object,
  electronCaptionMode: boolean,
  features: Object,
  firebaseUser: Object,
  fullScreen: boolean,
  inTwilioCalls: boolean,
  isInConversation: boolean,
  lightView: boolean,
  scribe: boolean,
  speakerListened: boolean,
  speakers: Object,
  status: Object,
  t: Function,
  theme: Object,
  toggleSideBar: Function,
  updateFullScreen: Function,
  user: Object,
  className?: string,
  history: Object,
  sidebarOpen: boolean,
}) {
  let ref;
  const onlineStatus = '';
  const [hideText, setHideText] = useState(false);

  const isOnline = () => {
    const backend = getBadge(backends);
    switch (backend.name) {
      case 'playground':
        return onlineStatus === 'playground';
      case 'production':
        return onlineStatus === 'production';
      default:
        return false;
    }
  };

  const handleUpdateFullScreen = e => {
    e.preventDefault();
    updateFullScreen(!fullScreen);
    toggleSideBar();
  };

  useEffect(() => {
    if (ref && ref.off) {
      ref.off();
    }
  }, []);

  useEffect(() => {
    if (user && firebaseUser) {
      Sentry.setUser({ email: firebaseUser.email, id: user.avaId });
    }
  }, [user, firebaseUser]);

  if (lightView) return null;
  const participants = (status || {}).participants || [];
  const transcriptOwnersId = (status || {}).transcriptOwners || [];
  const backgroundColor = theme.palette.secondary[theme.palette.type];
  const lineColor = theme.palette.divider;
  const backend = getBadge(backends);

  useEffect(() => {
    const handleResize = () => {
      setHideText(hideHeaderButtonsText(window.innerWidth, participants.length + transcriptOwnersId.length));
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [participants, transcriptOwnersId]);

  const isMac = getIsMac();
  let headerTop = 0;
  if (window.isElectron) {
    headerTop = isMac ? 28 : 32;
  }

  const showNewSidebarAndHeader = shouldShowNewSidebarAndHeader({
    electronCaptionMode,
    firebaseUser,
    history,
    isInConversation,
  });
  if (showNewSidebarAndHeader) {
    return (
      <HomeView
        className={className}
        style={{
          top: headerTop,
          width: sidebarOpen ? 'calc(100% - 272px)' : 'calc(100% - 90px)',
        }}
      />
    );
  }

  return (
    <>
      <AppBar
        className={classnames(classes.appBar, className)}
        style={{
          zIndex: 1300,
          WebkitAppRegion: 'no-drag',
          top: headerTop,
          display: fullScreen ? 'none' : undefined,
        }}
      >
        <Offline />
        <Toolbar
          style={{
            background: backgroundColor,
            height: 67,
            justifyContent: 'center',
            display: fullScreen ? 'none' : 'flex',
          }}
        >
          {firebaseUser && !history.location.pathname.includes('/login') && (
            // eslint-disable-next-line
            <div
              // {...getTogglerProps()}
              role="button"
              onClick={toggleSideBar}
              style={{
                outline: 'none',
                display: 'flex',
              }}
            >
              <MenuOutlinedIcon
                style={{
                  fontSize: 30,
                  marginRight: 20,
                  cursor: 'pointer',
                  color: theme.palette.text.primary,
                }}
              />
            </div>
          )}
          <Hidden xsDown>
            {isInConversation ? (
              <img className={classes.logo} src={AvaLogoImg} alt="ava logo" data-qa="avaLogo" />
            ) : (
              <a href="/" data-qa="avaLogo">
                <img className={classes.logo} src={AvaLogoImg} alt="ava logo" />
              </a>
            )}
          </Hidden>

          <ScribeSpeakerAudioSwitchContainer />
          <CallListContainer />
          {currentPlatform === 'web' && scribe && <AudioChunkBufferContainer />}
          {status && status.id && user && !isDefaultRoomId(status.id) && isInConversation && (
            <ParticipantList
              scribe={features.scribe && scribe}
              theme={theme}
              speakers={speakers}
              roomSpeakers={status.speakers}
              participants={participants}
              transcriptOwnersId={transcriptOwnersId}
              status={status}
              displayShortcuts={displayShortcuts}
              speakerListened={speakerListened}
              user={user}
              share
            />
          )}
          <div style={{ flexGrow: 1 }} />
          {displayable.dev && (
            <Hidden xsDown>
              <BackendBadge
                style={{
                  backgroundColor: backend.backgroundColor,
                }}
              >
                {backend.name}
                {features.ok} {displayable.dev}
              </BackendBadge>
            </Hidden>
          )}
          {scribe && status && status.conversationMode && (
            <BackendBadge
              style={{
                backgroundColor: getConversationModeColor(status.conversationMode),
              }}
            >
              {status.conversationMode}
            </BackendBadge>
          )}
          {scribe && status && status.title !== undefined && (
            <UpdateConvoMeta title={status.title} roLink={status.links ? status.links.ro : ''} />
          )}
          <Hidden xsDown>
            <div style={{ flexGrow: 1 }} />
            <div style={{ flexGrow: 1 }} />
          </Hidden>
          <div style={{ flexGrow: 1 }} />
          {status && status.id && !isDefaultRoomId(status.id) && user && isHost(status, user) && isInConversation && (
            <SelectAccuracyButton status={status} user={user} hideText={hideText} />
          )}
          {status &&
            status.id &&
            user &&
            !status.past &&
            transcriptOwnersId &&
            transcriptOwnersId.length > 0 &&
            !isDefaultRoomId(status.id) &&
            isInConversation && (
              <>{!inTwilioCalls && <AddParticipant scribe={scribe} user={user} hideText={hideText} />}</>
            )}
          {!window.isElectron && !authenticating && !user && !history.location.pathname.includes('/login') && (
            <div className={classes.joinConversation}>
              <JoinConversationButton />
            </div>
          )}
        </Toolbar>
      </AppBar>
      {!electronCaptionMode && (
        <FullScreenHamburger updateFullScreen={e => handleUpdateFullScreen(e)} fullScreen={fullScreen} />
      )}
    </>
  );
}

const mapStateToProps = ({
  scribeConversation: {
    conversationEnded,
    conversationStarted,
    inTwilioCalls,
    isInConversation,
    scribe,
    speakerListened,
    speakers,
    status,
  },
  firebase: { user: firebaseUser },
  scribeSession: { backends, captioner, currentPlatform, displayable, features, lightView, authenticating, sidebar },
  scribeTranscriptUI: { fullScreen, displayShortcuts, electronCaptionMode },
}) => ({
  authenticating,
  backends,
  conversationEnded,
  conversationStarted,
  currentPlatform,
  displayShortcuts,
  displayable,
  features,
  firebaseUser,
  fullScreen,
  electronCaptionMode,
  inTwilioCalls,
  isInConversation,
  lightView,
  scribe,
  speakerListened,
  speakers,
  status,
  user: captioner,
  sidebarOpen: sidebar,
});

// $FlowFixMe
export default withSnackbar(
  connect(mapStateToProps, {
    clearScribeConversationState,
    firebaseLogout,
    toggleSideBar,
    updateFullScreen,
  })(withRouter(withTheme(withStyles(styles)(makeComponentTrashable(withTranslation()(ScribeHeader))))))
);
